// Print Service Configuration
// Uses backend proxy to communicate with print server

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Send a print job to the print server (via backend proxy)
 * @param {string} printerName - Name of the printer (e.g., 'PM-246S')
 * @param {string} base64Data - PDF content as base64 string
 * @param {string} fileName - Optional filename for logs
 * @returns {Promise<{status: string, printer: string, file: string}>}
 */
export const sendPrintJob = async (printerName, base64Data, fileName = 'label.pdf') => {
  const response = await fetch(`${API_URL}/api/print/job`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      printerName: printerName,
      fileName: fileName,
      base64Data: base64Data
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Print API error: ${response.status}`);
  }

  return await response.json();
};

/**
 * Fetch all available printers from the fleet (via backend proxy)
 * @returns {Promise<Array<{id: string, name: string, description: string, machine: string, isOnline: boolean, jobCount: number}>>}
 */
export const fetchAvailablePrinters = async () => {
  const response = await fetch(`${API_URL}/api/print/fleet`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to fetch printers: ${response.status}`);
  }

  const data = await response.json();
  const printers = [];

  // Printers to ignore
  const ignoredPrinters = ['Microsoft Print to PDF'];

  // Add printers from master machine
  if (data.master && data.master.printers) {
    data.master.printers.forEach(printer => {
      if (ignoredPrinters.includes(printer.Name)) return;
      const isOnline = printer.isOnline === 1;
      printers.push({
        id: printer.Name, // Use printer name as ID since new API doesn't have numeric IDs
        name: printer.Name,
        description: `${printer.Name} (${data.master.machine})${isOnline ? '' : ' - Offline'}`,
        machine: data.master.machine,
        isOnline: isOnline,
        jobCount: printer.JobCount || 0
      });
    });
  }

  // Add printers from agent machines
  if (data.agents && Array.isArray(data.agents)) {
    data.agents.forEach(agent => {
      if (agent.printers) {
        agent.printers.forEach(printer => {
          if (ignoredPrinters.includes(printer.Name)) return;
          const isOnline = printer.isOnline === 1;
          printers.push({
            id: printer.Name, // Use printer name as ID
            name: printer.Name,
            description: `${printer.Name} (${agent.machine})${isOnline ? '' : ' - Offline'}`,
            machine: agent.machine,
            isOnline: isOnline,
            jobCount: printer.JobCount || 0
          });
        });
      }
    });
  }

  return printers;
};
